import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, Signal, viewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DsButtonVariants, Icons, IconScale, IconStyles } from '@levelaccess/design-system';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';

const DEFAULT_CORRECT_FIELDS_TEXT: string = 'label_correct_fields_below';

@Component({
  selector: 'app-common-file-dropbox',
  templateUrl: './common-file-dropbox.component.html',
  styleUrls: ['./common-file-dropbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommonFileDropboxComponent {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly IconStyles: typeof IconStyles = IconStyles;
  public readonly IconScale: typeof IconScale = IconScale;

  private MAX_FILES: number = 1;
  private MULTIPLE_FILE_ERROR: string = 'error_only_one_file_allowed_for_upload';

  @Input()
  public acceptedFileTypes: readonly string[];

  @Input()
  public acceptedFileError: string;

  @Input()
  public fileDataError: string;

  @Input()
  public form: UntypedFormGroup;

  @Input()
  public fileValid: boolean | undefined;

  @Input()
  public fileUploaded: boolean | undefined;

  @Input()
  public fileUploadedMessage: string;

  @Input()
  public fullWidth: boolean = false;

  @Input()
  public helperText?: string;

  /** Translation key for the failure heading. Defaults to the shared copy; when set, the heading is shown in red. */
  @Input()
  public failureHeading?: string;

  /** Translation key for the line above the error table. An empty value hides the line. */
  @Input()
  public correctFieldsText: string = DEFAULT_CORRECT_FIELDS_TEXT;

  @Output()
  public emitFile: EventEmitter<File>;

  @Output()
  public clickedValidate: EventEmitter<void>;

  @Output()
  public rebuildForm: EventEmitter<void>;

  public fileDropError: string | undefined;
  public file: File;

  private readonly addFileButton: Signal<ElementRef<HTMLElement> | undefined> = viewChild<unknown, ElementRef<HTMLElement>>(
    'addFileButton',
    { read: ElementRef },
  );
  private readonly uploadButton: Signal<ElementRef<HTMLElement> | undefined> = viewChild<unknown, ElementRef<HTMLElement>>(
    'uploadButton',
    { read: ElementRef },
  );
  private readonly failureHeadingElement: Signal<ElementRef<HTMLElement> | undefined> = viewChild<
    unknown,
    ElementRef<HTMLElement>
  >('failureHeadingElement', { read: ElementRef });

  constructor() {
    this.emitFile = new EventEmitter<File>();
    this.clickedValidate = new EventEmitter<void>();
    this.rebuildForm = new EventEmitter<void>();
  }

  public get hasDefaultCorrectFieldsText(): boolean {
    return this.correctFieldsText === DEFAULT_CORRECT_FIELDS_TEXT;
  }

  private isFileTypeValid(file: File): boolean {
    const type: string = this.acceptedFileTypes.find(
      (acceptedType: string): boolean =>
        acceptedType.toLocaleLowerCase() === file.type.toLocaleLowerCase() ||
        file.name.toLocaleLowerCase().endsWith(acceptedType.toLocaleLowerCase()),
    );
    return !SharedCommonUtility.isNullish(type);
  }

  public filesDropped(event: File[]): void {
    if (event.length !== this.MAX_FILES) {
      this.fileDropError = this.MULTIPLE_FILE_ERROR;
      return;
    }
    const file: File = event[0];
    if (!this.isFileTypeValid(file)) {
      this.fileDropError = this.acceptedFileError;
      return;
    }

    this.file = file;
    this.emitFile.emit(file);
    this.fileUploaded = false;
    this.fileDropError = undefined;
  }

  public filesChosen(event: Event): void {
    const bulkUploadElement: HTMLInputElement = event.target as HTMLInputElement;
    if (Array.from(bulkUploadElement.files).length !== this.MAX_FILES) {
      return;
    }
    const file: File = Array.from(bulkUploadElement.files)[0];
    if (!this.isFileTypeValid(file)) {
      this.fileDropError = this.acceptedFileError;
      return;
    }
    this.file = file;
    this.emitFile.emit(file);
    this.fileUploaded = false;
    this.fileDropError = undefined;
  }

  public cancelFile(): void {
    this.file = undefined;
    this.fileUploaded = undefined;
    this.rebuildForm.emit();
    this.emitFile.emit(null);
  }

  public validate(event: Event): void {
    event.preventDefault();
    this.clickedValidate.emit();
  }

  public focusAddFile(): void {
    this.addFileButton()?.nativeElement.focus();
  }

  public focusUpload(): void {
    this.uploadButton()?.nativeElement.focus();
  }

  public focusFailureHeading(): void {
    this.failureHeadingElement()?.nativeElement.focus();
  }
}
